import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Confirmation, Credentials } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Provider, Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form, Waiting } from '@beyond-js/ui/react';
import { ActingChoice, Check, NewPassword, Problem, Wordmark } from '@accounts/ui';
import { Carried } from './signin/carried';

/**
 * Setting a new password from a recovery message, for someone who cannot sign in.
 *
 * The message carries a token hash (`?token_hash=…&type=recovery&next=…`); a message written by the
 * provider's own template returns a recovery token in the address instead. Either is used once, by
 * the service, which checks the password against the one policy before it spends the link (ACC-02),
 * ends every other session of the account unless the person keeps them (ACC-03), tells them, and
 * signs them in. Where the person was going before they asked travels in `next` (ACC-11).
 *
 * A link that has expired or been used says exactly that and offers the way to ask for another,
 * because "that did not work" leaves someone locked out with nothing to try. While either field is empty
 * the form waits quietly (D183); a password under the rule, or a repetition that differs, is said.
 */
export function Reset() {
	const { text, client, language } = usePresence();
	const credentials = useMemo(() => new Credentials(client), [client]);
	const [parameters] = useSearchParams();
	const carried = useMemo(() => new Carried(parameters, globalThis.location.origin).parameters, [parameters]);

	const [proof, setProof] = useState<{ hash?: string; token?: string }>();
	const [reading, setReading] = useState(true);
	const [password, setPassword] = useState('');
	const [again, setAgain] = useState('');
	const [others, setOthers] = useState(true);
	// A recovery may follow someone else taking the account: ending what acts for the person is the default here (D196)
	const [acting, setActing] = useState(true);
	const [busy, setBusy] = useState(false);
	const [expired, setExpired] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [invalid, setInvalid] = useState<{ password?: string; again?: string }>({});

	useEffect(() => {
		const hash = parameters.get('token_hash');
		if (hash && parameters.get('type') === 'recovery') {
			setProof({ hash });
			setReading(false);
			return;
		}
		const returned = Provider.returned();
		if (returned.token) setProof({ token: returned.token });
		else if (returned.code) {
			void new Provider(config.provider, config.key)
				.exchange(returned.code)
				.then(token => setProof({ token }))
				// A provider that did not answer is not an expired link: saying so would send the person to
				// spend another one
				.catch(error => (Refusal.is(error) && error.unreachable ? setFailure(error) : setExpired(true)))
				.finally(() => setReading(false));
			return;
		} else setExpired(true);
		setReading(false);
		// Read once per page load: the link is taken out of the address
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Both fields hold something here: an empty one stopped the submission in `Form`, saying nothing
	async function submit() {
		const problems: { password?: string; again?: string } = {};
		if ([...password].length < 10) problems.password = text('password.weak.short', { min: 10 });
		if (password !== again) problems.again = text('reset.mismatch');
		setInvalid(problems);
		if (Object.keys(problems).length || !proof) return;

		setBusy(true);
		setFailure(undefined);
		try {
			const answer = await credentials.reset(proof, password, !others, acting);
			setPassword('');
			setAgain('');
			const confirmation = new Confirmation(carried, config.authority);
			if (answer.factor) {
				globalThis.location.assign(`/signin?${carried}`);
				return;
			}
			// On to where the person was going, else to their sign-in page, which says what ended
			globalThis.location.assign(confirmation.handoff ?? (carried.toString() ? confirmation.signin : `/security?reset=${answer.ended ?? 0}${answer.access ? `&apps=${answer.access.apps}&tokens=${answer.access.tokens}` : ''}`));
		} catch (error) {
			// A password is never kept as a draft, whatever happened
			setPassword('');
			setAgain('');
			if (Refusal.is(error) && error.code === 'PASSWORD_WEAK') setInvalid({ password: text(error.key, error.values) });
			else if (Refusal.is(error) && error.code === 'UNAUTHENTICATED') setExpired(true);
			else setFailure(error);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
			<header className="flex flex-col gap-3">
				<Wordmark label="Accounts" />
				<div>
					<h1 className="text-title-1 text-ink">{text('reset.title')}</h1>
					<p className="mt-1 text-body text-ink-muted">{text('reset.lead')}</p>
				</div>
			</header>

			{/* Reading the link is the screen's first read, with the shared wait the opening covers (D185) */}
			{reading ? <Waiting language={language} kind="lines" rows={3} /> : null}
			{failure ? <Problem error={failure} /> : null}
			{expired ? (
				<Callout tone="danger" live title={text('reset.expired')} actions={<Link className="underline" to={`/signin?${carried}`}>{text('reset.ask')}</Link>} />
			) : null}

			{proof && !expired ? (
				<Form
					className="flex flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void submit();
					}}
				>
					<NewPassword label={text('reset.password')} value={password} onChange={setPassword} error={invalid.password} required />
					<Field label={text('reset.again')} error={invalid.again}>
						<input type="password" autoComplete="new-password" required value={again} onChange={event => setAgain(event.target.value)} />
					</Field>
					<Check label={text('reset.others')} checked={others} onChange={setOthers} />
					<ActingChoice checked={acting} onChange={setActing} />
					<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('action.saving') }}>
						{text('reset.submit')}
					</Button>
				</Form>
			) : null}
		</div>
	);
}
